import { faMinusCircle, faPlusCircle } from '@fortawesome/free-solid-svg-icons';
import React, { Dispatch, useMemo, useState } from 'react';
import { useIntl } from 'react-intl/dist';
import styled from 'styled-components';

import { AccordionPanel } from 'app-components';
import { fetchEnrollmentDetails } from '../../../../actions/memberPortal/enrollmentsActionCreators';
import { RELATIONSHIP_TYPES } from '../../../../constants/common/relationShipTypes';
import { useAppDispatch } from '../../../../hooks';
import { formatDateString } from '../../../../utils/dates/dateMethods';
import { getEnrolleeOrderList } from '../common/enrollmentUtils';
import { IEnrollment } from '../common/types';
import DetailedFields from './DetailedFields';
import EnrolleeTable from './EnrolleTable';
import EventHistoryTable from './EventHistoryTable';
import InfoSection from './InfoSection';

const StyledContainer = styled.div`
    margin-top: 5rem;
    .header-container {
        padding: 0;
        svg {
            width: 1.7rem;
        }
        a {
            display: flex;
        }

        .fa-minus-circle {
            color: ${({ theme }) => theme.dangerMain};
        }
        .header {
            padding: 2px 0 0 3px;
        }
    }
    .content {
        border: none;
        padding: 10px;
        > div {
            border-top: 1px solid ${({ theme }) => theme.gray8};
        }
    }
    > div {
        border: 0;
    }
`;

const StyledDetailsContainer = styled.div`
    margin-top: 0.7rem;
    padding: 1rem 0;
    transition: opacity 0.5s ease-in;
    opacity: 1;

    .header-container,
    .content {
        padding: 0;
    }
`;

const StyledLink = styled.a`
    display: flex;
    align-items: center;
    font-weight: 600;
`;

const StyledRow = styled.div`
    display: flex;
    gap: 2rem;
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;
    }
`;

interface IAdditionalInformationProps {
    enrollment: IEnrollment;
    dataTestId: string;
}

const AdditionalInformation: React.FC<IAdditionalInformationProps> = ({ enrollment, dataTestId }) => {
    const intl = useIntl();
    const dispatch: Dispatch<any> = useAppDispatch();
    const [enrollmentDetails, setEnrollmentDetails] = useState<any>({});
    // const { enrollmentDetails } = useSelector((state: { memberEnrollment: any }) => state.memberEnrollment);

    const userCount = useMemo(() => {
        return enrollment.enrolleeDataDtoList.reduce(
            (counts, enrollee) => {
                const relationship = enrollee.relationshipToSubscriber.toUpperCase();
                if (relationship === RELATIONSHIP_TYPES.SELF.value) {
                    counts.selfCount++;
                } else if (relationship === RELATIONSHIP_TYPES.SPOUSE.value) {
                    counts.spouseCount++;
                } else {
                    counts.dependentCount++;
                }
                return counts;
            },
            { selfCount: 0, spouseCount: 0, dependentCount: 0 },
        );
    }, [enrollment.enrolleeDataDtoList]);

    const renderDetailedFields = useMemo(
        () =>
            (
                fields: {
                    label: string;
                    value: string;
                    dataTestId: string;
                    showColon?: boolean;
                    isVisible?: boolean;
                }[],
            ) => {
                return fields.map((field, index) => (
                    <React.Fragment key={index}>
                        {field?.isVisible !== false && (
                            <DetailedFields
                                label={field.label}
                                value={field.value}
                                dataTestId={field.dataTestId}
                                showColon={field.showColon}
                            />
                        )}
                    </React.Fragment>
                ));
            },
        [],
    );

    const additionalFields = useMemo(
        () => [
            [
                {
                    label: 'member.enrollment_list.premiumhistory.ratiingArea',
                    value: enrollment.ratingArea,
                    dataTestId: 'rating-area-field',
                    showColon: true,
                },
                {
                    label: 'member.enrollment_list.premiumhistory.enrollerName',
                    value: enrollment.agentName,
                    dataTestId: 'enroller-name-field',
                    isVisible: !!enrollment.agentName,
                    showColon: true,
                },
                {
                    label: 'label.address.home',
                    value: `${enrollment.homeAddress1}${
                        enrollment.homeAddress2 ? ', ' + enrollment.homeAddress2 : ''
                    }, ${enrollment.homeCity}, ${enrollment.homeState}, ${enrollment.homeZipcode} (${
                        enrollment.homeCountyCode
                    })`,
                    dataTestId: 'home-address-field',
                    showColon: true,
                },
                {
                    label: 'member.enrollment_list.premiumhistory.primaryTaxFiler',
                    value: ` ${enrollment.ptfFirstName} ${
                        enrollment.ptfMiddleName ? enrollment.ptfMiddleName + ' ' : ''
                    }${enrollment.ptfLastName}`,
                    dataTestId: 'primary-tax-filer-field',
                    showColon: true,
                },
            ],
            [
                {
                    label: 'member.enrollment_list.premiumhistory.ratingAreaEffectivedate',
                    value: formatDateString(enrollment.ratingAreaEffectiveDate) || '',
                    dataTestId: 'rating-area-effective-date-field',
                    showColon: true,
                },
                {
                    label: 'member.enrollment_list.premiumhistory.enrolleeTPANumber',
                    value: enrollment.agentTpaNumber,
                    dataTestId: 'enroller-npa-field',
                    isVisible: !!enrollment.agentTpaNumber,
                    showColon: true,
                },
                {
                    label: 'label.address.mailing',
                    value: `${enrollment.mailingAddress1}${
                        enrollment.mailingAddress2 ? ', ' + enrollment.mailingAddress2 : ''
                    }, ${enrollment.mailingCity}, ${enrollment.mailingState}, ${enrollment.mailingZipcode} ${
                        enrollment?.mailingCountyCode ? `(${enrollment.mailingCountyCode})` : ''
                    }`,
                    dataTestId: 'mailing-address-field',
                    showColon: true,
                },
            ],
        ],
        [enrollment],
    );

    const onToggle = async isVisible => {
        if (!isVisible) {
            const payload = new FormData();
            payload.append('enrollmentId', enrollment.enrollmentId.toString());

            const data = await fetchEnrollmentDetails(dispatch, payload);
            setEnrollmentDetails(data);
        }
    };

    const getEnrolleeDataDtoList = useMemo(() => {
        const enrolee = getEnrolleeOrderList(enrollment.enrolleeDataDtoList);
        return enrolee;
    }, [enrollment.enrolleeDataDtoList]);

    return (
        <StyledContainer className="" data-testid={dataTestId} index={enrollment.enrollmentId}>
            <AccordionPanel
                id={'additionnalInfo'}
                header={
                    <StyledLink className="color-link">
                        {intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.additionnalInfo' })}
                    </StyledLink>
                }
                isLeftToggle={true}
                expandedIcon={faMinusCircle}
                collapsedIcon={faPlusCircle}
                onToggle={onToggle}
                isHeaderClickable={true}
            >
                <StyledDetailsContainer dataTestId="details-container">
                    <StyledRow>
                        <InfoSection>{renderDetailedFields(additionalFields[0])}</InfoSection>
                        <InfoSection>{renderDetailedFields(additionalFields[1])}</InfoSection>
                    </StyledRow>
                    <EnrolleeTable
                        enrolleeDataDtoList={getEnrolleeDataDtoList}
                        heading={`Enrollees (${userCount.selfCount} primary, ${userCount.spouseCount} spouse, ${userCount.dependentCount} dependent)`}
                    />
                    <EventHistoryTable
                        enrollmentHistoryList={enrollmentDetails?.enrollmentEventHistoryDTOList}
                        heading={'member.enrollment_list.premiumhistory.enrollmentHistory'}
                        enrollmentId={enrollment.enrollmentId}
                    />
                </StyledDetailsContainer>
            </AccordionPanel>
        </StyledContainer>
    );
};

export default AdditionalInformation;
